import { Effect, Equal, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { Rule, makeRules, validate } from "foldkit/fieldValidation";
import * as Navigation from "foldkit/navigation";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle, start } from "../../api/asyncData";
import { asOfTag } from "../../api/client";
import { createEntries } from "../../api/commands/addEntries";
import { deleteEntry } from "../../api/commands/entries";
import { fetchWeek, skip, swap } from "../../api/commands/keepInTouch";
import {
	addRecord,
	deleteRecord,
	getRecords,
	getSearch,
	mergeRecords,
} from "../../api/commands/records";
import { type ApiError, toMessage } from "../../api/errors";
import { RecordType } from "../../api/schemas/common";
import type { RecordSummary } from "../../api/schemas/records";
import { makeTrack } from "../../app/analytics";
import { type Route, asOfOf, toHref } from "../../app/routes";
import { i18next } from "../../i18n";
import * as AddEntry from "../../widgets/addEntry";
import * as Attributes from "../../widgets/attributes";
import { Dialog } from "../../widgets/dialog";
import { FocusEditor, SetEditorText } from "../../widgets/editor";
import * as Cards from "../../widgets/keepInTouchCards";
import * as Picker from "../../widgets/recordPicker";
import * as Tags from "../../widgets/tags";
import { RECORD_FIELD_MAX } from "../../widgets/validatedField";
import type { Api, Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import {
	type Capture,
	type Form,
	type Model,
	type Results,
	type Row,
	emptyForm,
} from "./model";
import { parseSearchInput } from "./searchInput";

// COMMANDS

export const Search = getSearch.command(Message.CompletedSearch, asOfTag);
export const FetchRecords = getRecords.command(
	Message.CompletedFetchRecords,
	({ search, cursor, asOf, generation, result }) =>
		Message.CompletedFetchRecords({
			search,
			cursor,
			asOf: Option.fromNullishOr(asOf),
			generation,
			result,
		}),
);
export const DeleteRecord = deleteRecord.command(
	Message.CompletedDeleteRecord,
	({ nodeId, generation, result }) =>
		Message.CompletedDeleteRecord({ nodeId, generation, result }),
);
export const DeleteEntry = deleteEntry.command(
	Message.CompletedDeleteEntry,
	({ knotId, generation, result }) =>
		Message.CompletedDeleteEntry({ knotId, generation, result }),
);
export const MergeRecords = mergeRecords.command(
	Message.CompletedMerge,
	({ targetNodeId, sourceNodeId, generation, result }) =>
		Message.CompletedMerge({ targetNodeId, sourceNodeId, generation, result }),
);

export const FetchWeek = Command.define("FetchWeek", {
	args: { asOf: S.Option(S.String), generation: S.Number },
	messages: [Message.CompletedFetchWeek],
	execute: ({ asOf, generation }) =>
		fetchWeek.pipe(
			Effect.map((result) =>
				Message.CompletedFetchWeek({ asOf, generation, result }),
			),
		),
});
export const SkipCard = Command.define("SkipCard", {
	args: { recordId: S.String, generation: S.Number },
	messages: [Message.CompletedSkip],
	execute: ({ recordId, generation }) =>
		skip(recordId).pipe(
			Effect.map((result) =>
				Message.CompletedSkip({ recordId, generation, result }),
			),
		),
});
/** Next-ranked swap only; choosing a replacement is the Keep in touch page's (U7). */
export const SwapCard = Command.define("SwapCard", {
	args: { recordId: S.String, generation: S.Number },
	messages: [Message.CompletedSwap],
	execute: ({ recordId, generation }) =>
		swap(recordId, null).pipe(
			Effect.map((result) =>
				Message.CompletedSwap({ recordId, generation, result }),
			),
		),
});
export const Track = makeTrack(Message.CompletedTrack);

/** Search input id on the Home and Records pages; the root Cmd-K shortcut focuses it (U6). */
export const SEARCH_INPUT_ID = "home-search";

/** R11: six most recently touched records. */
export const RECENT_LIMIT = 6;
export const FetchRecent = Command.define("FetchRecent", {
	args: { generation: S.Number },
	messages: [Message.CompletedFetchRecent],
	execute: ({ generation }) =>
		getRecords
			.run({
				search: "",
				cursor: 0,
				asOf: null,
				sort: "recent",
				limit: RECENT_LIMIT,
			})
			.pipe(
				Effect.map((result) =>
					Message.CompletedFetchRecent({ generation, result }),
				),
			),
});

/** KTD2: the write is shared with the record page; only the completion message is ours. */
export const AddEntries = Command.define("AddEntries", {
	args: { nodeId: S.String, request: AddEntry.Request, generation: S.Number },
	messages: [Message.CompletedAddEntries],
	execute: ({ nodeId, request, generation }) =>
		createEntries(nodeId, request).pipe(
			Effect.map((result) =>
				Message.CompletedAddEntries({ nodeId, request, generation, result }),
			),
		),
});

/** v1 create-mode submit: reserved keys fold into `attributes` ahead of the editor's rows; the timestamp is taken at issue time. */
export const CreateRecord = Command.define("CreateRecord", {
	args: {
		name: S.String,
		type: RecordType,
		subtitle: S.String,
		email: S.String,
		description: S.String,
		tags: S.Array(S.String),
		attributes: S.Record(S.String, S.String),
		generation: S.Number,
	},
	messages: [Message.CompletedAddRecord],
	execute: ({
		name,
		type,
		subtitle,
		email,
		description,
		tags,
		attributes: custom,
		generation,
	}) =>
		Effect.gen(function* () {
			const attributes = {
				...(subtitle ? { subtitle } : {}),
				...(email ? { email } : {}),
				...custom,
			};
			const result = yield* addRecord.run({
				data: {
					id: "",
					circleId: "",
					name,
					subtitle: subtitle || undefined,
					description,
					tags,
					type,
					avatars: [],
					attributes:
						Object.keys(attributes).length > 0 ? attributes : undefined,
					timestamp: new Date().toISOString(),
				},
			});
			return Message.CompletedAddRecord({ generation, result });
		}),
});

/** v1: `setSearchParams(..., { replace: true })` mirroring the search text into `q`. */
export const SyncQuery = Command.define("SyncSearchQuery", {
	args: { url: S.String },
	messages: [Message.CompletedSyncQuery],
	execute: ({ url }) =>
		Navigation.replaceUrl(url).pipe(Effect.as(Message.CompletedSyncQuery())),
});

// RULES (v1 react-hook-form registrations)

export const nameRules = makeRules({
	required: "This field is required",
	rules: [
		Rule.maxLength(
			RECORD_FIELD_MAX.name,
			`Name must be ${RECORD_FIELD_MAX.name} characters or fewer`,
		),
	],
});
export const subtitleRules = makeRules({
	rules: [
		Rule.maxLength(
			RECORD_FIELD_MAX.subtitle,
			`Subtitle must be ${RECORD_FIELD_MAX.subtitle} characters or fewer`,
		),
	],
});
export const emailRules = makeRules({
	rules: [
		Rule.maxLength(
			RECORD_FIELD_MAX.email,
			`Email must be ${RECORD_FIELD_MAX.email} characters or fewer`,
		),
	],
});
export const descriptionRules = makeRules({
	rules: [
		Rule.maxLength(
			RECORD_FIELD_MAX.description,
			`Description must be ${RECORD_FIELD_MAX.description} characters or fewer`,
		),
	],
});

// HELPERS

type Return = PageReturn<Model, Message>;

export const initial = (): Model => ({
	page: "Searched",
	searchText: "",
	searchType: "all",
	knotType: "all",
	results: AsyncData.Idle(),
	selected: [],
	deleteDialog: Dialog.init({ id: "confirm_delete_dialog" }),
	snapshot: Option.none(),
	pendingDeletes: 0,
	deleteError: Option.none(),
	merging: false,
	addDialog: Dialog.init({ id: "add_record" }),
	form: emptyForm,
	saving: false,
	keepInTouch: AsyncData.Idle(),
	keepInTouchError: Option.none(),
	capture: {
		picker: Picker.init,
		addEntry: AddEntry.init(),
		fromCard: Option.none(),
	},
	recent: AsyncData.Idle(),
});

const editCapture = (model: Model, f: (capture: Capture) => Capture): Model =>
	evo(model, { capture: f });

/** KTD1: Home, now, empty text, default filters. */
export const isFeed = (model: Model, shared: Shared): boolean =>
	shared.route._tag === "Home" &&
	Option.isNone(shared.asOf) &&
	model.searchText === "" &&
	model.searchType === "all" &&
	model.knotType === "all";

const settled = <A, E>(data: AsyncData.AsyncData<A, E>): boolean =>
	AsyncData.isSuccess(data) || AsyncData.isFailure(data);

/** Both Home fetches have answered, either way; nothing below the strip renders before that. */
export const isFeedSettled = (model: Model): boolean =>
	settled(model.keepInTouch) && settled(model.recent);

/**
 * Nothing in the circle at all (R14, KTD7): the week has no cards, tracked or
 * paused records and the recent list is empty. A failed recent fetch reads as
 * empty here, since its section hides on failure anyway.
 */
export const isEmptyCircle = (model: Model): boolean =>
	AsyncData.isSuccess(model.keepInTouch) &&
	model.keepInTouch.data.cards.length === 0 &&
	model.keepInTouch.data.tracked.length === 0 &&
	model.keepInTouch.data.paused.length === 0 &&
	(AsyncData.isFailure(model.recent) ||
		(AsyncData.isSuccess(model.recent) && model.recent.data.length === 0));

const emptyResults: Results = { items: [], nextCursor: Option.none() };
export const rows = (model: Model): ReadonlyArray<Row> =>
	AsyncData.getOrElse(model.results, () => emptyResults).items;

export const recordToRow = (record: RecordSummary): Row => ({
	type: "node",
	title: record.name,
	subtitle: record.subtitle ?? "",
	id: record.id,
	foundInfo: [],
	avatars: record.avatars,
	nodeType: record.type,
	tags: record.tags,
});

/** Time travel filters client-side by name/subtitle substring and tag intersection (v1). */
export const visibleRows = (
	model: Model,
	shared: Shared,
): ReadonlyArray<Row> => {
	const all = rows(model);
	if (Option.isNone(shared.asOf)) return all;
	const { text, tags } = parseSearchInput(model.searchText);
	const term = text.toLowerCase();
	return all.filter(
		(row) =>
			(term === "" ||
				row.title.toLowerCase().includes(term) ||
				row.subtitle.toLowerCase().includes(term)) &&
			tags.every((tag) =>
				(row.tags ?? []).some((t) => t.toLowerCase() === tag.toLowerCase()),
			),
	);
};

/** The Subscription gate: `Some(text)` while there is text to debounce (KTD12). */
export const searchDebounceDependencies = (model: Model) => ({
	maybeText:
		model.searchText === ""
			? Option.none<string>()
			: Option.some(model.searchText),
});

const searchArgs = (model: Model, cursor: number, shared: Shared) => {
	const { text, tags } = parseSearchInput(model.searchText);
	return {
		search: text,
		cursor,
		searchType: model.searchType,
		knotType: model.knotType === "all" ? null : model.knotType,
		tags,
		asOf: shared.asOf,
		generation: shared.generation,
	};
};

const fetchPage = (model: Model, cursor: number, shared: Shared): Return => ({
	model: evo(model, { results: start }),
	commands: [
		Option.match(shared.asOf, {
			onNone: () => Search(searchArgs(model, cursor, shared)),
			onSome: (asOf) =>
				FetchRecords({
					search: "",
					cursor,
					asOf,
					generation: shared.generation,
				}),
		}),
	],
});

/** KTD8: the week is fetched on Home only, and never under time travel (its read writes). */
const loadWeek = (model: Model, route: Route, shared: Shared): Return =>
	route._tag === "Home" && Option.isNone(asOfOf(route))
		? {
				model: evo(model, { keepInTouch: start }),
				commands: [
					FetchWeek({ asOf: shared.asOf, generation: shared.generation }),
				],
			}
		: { model: evo(model, { keepInTouch: () => AsyncData.Idle() }) };

/** R1, R11: recent rides beside the week on Home; both go Idle elsewhere. */
const loadFeed = (model: Model, route: Route, shared: Shared): Return => {
	const week = loadWeek(model, route, shared);
	return route._tag === "Home" && Option.isNone(asOfOf(route))
		? {
				model: evo(week.model, { recent: start }),
				commands: [
					...(week.commands ?? []),
					FetchRecent({ generation: shared.generation }),
				],
			}
		: { model: evo(week.model, { recent: () => AsyncData.Idle() }) };
};

const isConflict = (error: ApiError): boolean =>
	error._tag === "Http" && error.status === 409;

/** A card action settled: a 409 gets its translated message; skip refetches either way, swap only on success. */
const completedCardAction = (
	model: Model,
	shared: Shared,
	generation: number,
	result: Result.Result<unknown, ApiError>,
	conflictKey: string,
	refetchOnConflict: boolean,
): Return => {
	if (generation !== shared.generation) return { model };
	return Result.match(result, {
		onSuccess: () => loadWeek(model, shared.route, shared),
		onFailure: (error) => {
			const failed = evo(model, {
				keepInTouchError: () =>
					Option.some(
						isConflict(error) ? i18next.t(conflictKey) : toMessage(error),
					),
			});
			return isConflict(error) && refetchOnConflict
				? loadWeek(failed, shared.route, shared)
				: { model: failed };
		},
	});
};

/** A new query: selection cleared (v1 effect on text/type/knotType/tags), first page fetched; nothing in feed mode (R1). */
const search = (model: Model, shared: Shared): Return => {
	const cleared = evo(model, { selected: () => [] });
	return isFeed(cleared, shared)
		? { model: cleared }
		: fetchPage(cleared, 0, shared);
};

const fold = (model: Model, cursor: number, page: Results): Model =>
	evo(model, {
		results: (results) =>
			settle(
				results,
				Result.succeed(
					cursor === 0
						? page
						: {
								items: [...rows(model), ...page.items],
								nextCursor: page.nextCursor,
							},
				),
			),
	});

const toResults = <A>(
	page: { data: ReadonlyArray<A>; nextCursor?: number | undefined },
	toRow: (a: A) => Row,
): Results => ({
	items: page.data.map(toRow),
	nextCursor: Option.fromNullishOr(page.nextCursor),
});

const routeText = (route: Route): string =>
	route._tag === "Home" || route._tag === "Records"
		? Option.getOrElse(route.q, () => "")
		: "";

/** A `q` change from outside (the Tags page link) replaces the text; our own URL sync is a no-op. */
const withTextSynced = (model: Model, route: Route, shared: Shared): Return => {
	const text = routeText(route);
	return text === model.searchText
		? { model }
		: search(evo(model, { searchText: () => text }), shared);
};

type Step = (model: Model) => Return;

/** Runs `next` on the model `first` produced and keeps both Command lists. */
const andThen =
	(first: Step, next: Step): Step =>
	(model) => {
		const a = first(model);
		const b = next(a.model);
		return {
			model: b.model,
			commands: [...(a.commands ?? []), ...(b.commands ?? [])],
		};
	};

/** After a write (R2): the list as before, and on Home the week and recent too. */
const refresh = (model: Model, shared: Shared): Return =>
	andThen(
		(m) => search(m, shared),
		(m) => loadFeed(m, shared.route, shared),
	)(model);

const readDeleteDialog = (model: Model) => Option.some(model.deleteDialog);
const writeDeleteDialog = (model: Model, deleteDialog: Dialog.Model) =>
	evo(model, { deleteDialog: () => deleteDialog });
const toDeleteDialogMessage = (message: Dialog.Message) =>
	Message.GotDeleteDialogMessage({ message });
const foldDeleteDialog = Update.foldChild({
	update: (dialog: Dialog.Model, message: Dialog.Message) =>
		Dialog.update(dialog, message),
	read: readDeleteDialog,
	write: writeDeleteDialog,
	toParentMessage: toDeleteDialogMessage,
	foldOutMessage: () => (model: Model) => ({ model }),
});
const openDeleteDialog: Step = Update.foldChildStep({
	update: Dialog.open,
	read: readDeleteDialog,
	write: writeDeleteDialog,
	toParentMessage: toDeleteDialogMessage,
	foldOutMessage: () => (model: Model) => ({ model }),
});
const closeDeleteDialog: Step = Update.foldChildStep({
	update: Dialog.close,
	read: readDeleteDialog,
	write: writeDeleteDialog,
	toParentMessage: toDeleteDialogMessage,
	foldOutMessage: () => (model: Model) => ({ model }),
});

const readAddDialog = (model: Model) => Option.some(model.addDialog);
const writeAddDialog = (model: Model, addDialog: Dialog.Model) =>
	evo(model, { addDialog: () => addDialog });
const toAddDialogMessage = (message: Dialog.Message) =>
	Message.GotAddDialogMessage({ message });
const foldAddDialog = Update.foldChild({
	update: (dialog: Dialog.Model, message: Dialog.Message) =>
		Dialog.update(dialog, message),
	read: readAddDialog,
	write: writeAddDialog,
	toParentMessage: toAddDialogMessage,
	foldOutMessage: () => (model: Model) => ({ model }),
});
const openAddDialog: Step = Update.foldChildStep({
	update: Dialog.open,
	read: readAddDialog,
	write: writeAddDialog,
	toParentMessage: toAddDialogMessage,
	foldOutMessage: () => (model: Model) => ({ model }),
});
const closeAddDialog: Step = Update.foldChildStep({
	update: Dialog.close,
	read: readAddDialog,
	write: writeAddDialog,
	toParentMessage: toAddDialogMessage,
	foldOutMessage: () => (model: Model) => ({ model }),
});

const editForm = (model: Model, f: (form: Form) => Form): Model =>
	evo(model, { form: f });

export const recordSchema = (shared: Shared) =>
	Attributes.schemaFor(shared, "record");

const foldTags = Update.foldChild({
	update: (child: Tags.Model, message: Tags.Message) =>
		Tags.update(child, message),
	read: (model: Model) => Option.some(model.form.tagBox),
	write: (model: Model, tagBox: Tags.Model) =>
		editForm(model, (form) => ({ ...form, tagBox })),
	toParentMessage: (message: Tags.Message) =>
		Message.GotTagsMessage({ message }),
	foldOutMessage:
		(out: Tags.OutMessage) =>
		(model: Model): Return =>
			Tags.OutMessage.match(out, {
				// v1 `onAdd`: invalid labels show the rule and add nothing.
				Selected: ({ value }) => {
					const tag = Tags.sanitizeTag(value);
					return {
						model: editForm(model, (form) =>
							tag === undefined
								? {
										...form,
										tagError: Option.some(Tags.TAG_VALIDATION_MESSAGE),
									}
								: {
										...form,
										tagError: Option.none(),
										tags: [...form.tags, tag],
									},
						),
					};
				},
				ClearedSelection: () => ({ model }),
			}),
});

const foldPicker = (shared: Shared) =>
	Update.foldChild({
		update: (child: Picker.Model, message: Picker.Message) =>
			Picker.update(child, message, shared),
		read: (model: Model) => Option.some(model.capture.picker),
		write: (model: Model, picker: Picker.Model) =>
			editCapture(model, (c) => ({ ...c, picker })),
		toParentMessage: (message: Picker.Message) =>
			Message.GotPickerMessage({ message }),
	});

/** KTD8: the card association survives only while the chosen record is that card's. */
const syncFromCard = (model: Model): Model =>
	editCapture(model, (c) => ({
		...c,
		fromCard: Option.filter(c.fromCard, (id) =>
			Option.exists(c.picker.chosen, (chosen) => chosen.id === id),
		),
	}));

/** Built per message so a submit with no record can restore the pre-submit widget (R9). */
const foldAddEntry = (before: Model, shared: Shared) =>
	Update.foldChild({
		update: (child: AddEntry.Model, message: AddEntry.Message) =>
			AddEntry.update(child, message),
		read: (model: Model) => Option.some(model.capture.addEntry),
		write: (model: Model, addEntry: AddEntry.Model) =>
			editCapture(model, (c) => ({ ...c, addEntry })),
		toParentMessage: (message: AddEntry.Message) =>
			Message.GotAddEntryMessage({ message }),
		foldOutMessage:
			(out: AddEntry.OutMessage) =>
			(model: Model): Return =>
				AddEntry.OutMessage.match(out, {
					Submitted: ({ request }) =>
						Option.match(model.capture.picker.chosen, {
							onNone: () => ({
								model: editCapture(model, (c) => ({
									...c,
									addEntry: before.capture.addEntry,
									picker: Picker.requireChoice(c.picker),
								})),
							}),
							onSome: (chosen) => ({
								model,
								commands: [
									AddEntries({
										nodeId: chosen.id,
										request,
										generation: shared.generation,
									}),
								],
							}),
						}),
				}),
	});

const selectedNodeIds = (model: Model): ReadonlyArray<string> =>
	model.selected.filter((id) =>
		rows(model).some((row) => row.id === id && row.type === "node"),
	);

const completedDelete = (
	model: Model,
	shared: Shared,
	id: string,
	generation: number,
	result: Result.Result<unknown, ApiError>,
): Return => {
	if (generation !== shared.generation) return { model };
	const pending = model.pendingDeletes - 1;
	const failed = Result.match(result, {
		onSuccess: () => model,
		onFailure: (error) =>
			evo(model, {
				deleteError: () => Option.some(toMessage(error)),
				// Only this row comes back (in its old position); rows whose delete succeeded stay gone.
				results: (results) =>
					Option.match(model.snapshot, {
						onNone: () => results,
						onSome: (snapshot) => {
							const shown = new Set(rows(model).map((row) => row.id));
							return settle(
								results,
								Result.succeed({
									...snapshot,
									items: snapshot.items.filter(
										(row) => row.id === id || shown.has(row.id),
									),
								}),
							);
						},
					}),
			}),
	});
	const settled = evo(failed, { pendingDeletes: () => pending });
	if (pending > 0) return { model: settled };
	// v1: invalidate search, clear selection, close the dialog. A failure keeps the
	// dialog open with the error but still refetches so the list matches the server.
	const done = evo(settled, { snapshot: () => Option.none() });
	return Option.isSome(done.deleteError)
		? refresh(done, shared)
		: andThen(closeDeleteDialog, (m) => refresh(m, shared))(done);
};

const submitAddRecord = (model: Model, shared: Shared): Return => {
	const form = model.form;
	const name = validate(nameRules)(form.name.value);
	const subtitle = validate(subtitleRules)(form.subtitle.value);
	const email = validate(emailRules)(form.email.value);
	const description = validate(descriptionRules)(form.description.value);
	const validated = editForm(model, (f) => ({
		...f,
		name,
		subtitle,
		email,
		description,
	}));
	if ([name, subtitle, email, description].some((f) => f._tag === "Invalid")) {
		return { model: validated };
	}
	return {
		model: evo(validated, { saving: () => true }),
		commands: [
			CreateRecord({
				name: name.value,
				type: form.type,
				subtitle: subtitle.value.trim(),
				email: email.value,
				description: description.value,
				tags: Array.from(new Set(form.tags)),
				attributes: Attributes.flattenRows(form.attributes) ?? {},
				generation: shared.generation,
			}),
		],
	};
};

// PAGE

export const page: Page<Model, Message> = {
	init: (route, shared) =>
		andThen(
			(m) => search(evo(m, { searchText: () => routeText(route) }), shared),
			(m) => loadFeed(m, route, shared),
		)(initial()),
	changedRoute: (model, previous, next, shared) =>
		Equal.equals(asOfOf(previous), asOfOf(next))
			? withTextSynced(model, next, shared)
			: andThen(
					(m) => search(m, shared),
					(m) => loadFeed(m, next, shared),
				)(model),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			ChangedSearchText: ({ value }) => {
				const url = toHref({
					...(shared.route._tag === "Records"
						? shared.route
						: { _tag: "Home" as const, asOf: shared.asOf }),
					q: value === "" ? Option.none() : Option.some(value),
				});
				const typed = evo(model, {
					searchText: () => value,
					selected: () => [],
				});
				const sync: Return = { model: typed, commands: [SyncQuery({ url })] };
				// Empty text and time travel need no debounce: nothing (or only the client) runs.
				return value !== "" || Option.isSome(shared.asOf)
					? sync
					: andThen(
							() => sync,
							(m) => search(m, shared),
						)(typed);
			},
			SearchDebounced: ({ text }) =>
				text === model.searchText && Option.isNone(shared.asOf)
					? search(model, shared)
					: { model },
			CompletedSyncQuery: () => ({ model }),
			// v1: leaving "knot" resets the subtype to "all".
			ChangedSearchType: ({ value }) =>
				search(
					evo(model, {
						searchType: () => value,
						knotType: (knotType) => (value === "knot" ? knotType : "all"),
					}),
					shared,
				),
			ChangedEntryType: ({ value }) =>
				search(evo(model, { knotType: () => value }), shared),
			CompletedSearch: ({ cursor, result, generation, asOf, ...args }) => {
				const current = searchArgs(model, cursor, shared);
				const stale =
					generation !== shared.generation ||
					!Equal.equals(asOf, shared.asOf) ||
					args.search !== current.search ||
					args.searchType !== current.searchType ||
					args.knotType !== current.knotType ||
					!Equal.equals(args.tags, current.tags);
				if (stale) return { model };
				return {
					model: Result.match(result, {
						onSuccess: (page) =>
							fold(
								model,
								cursor,
								toResults(page, (row) => row),
							),
						onFailure: (error) =>
							evo(model, { results: (r) => settle(r, Result.fail(error)) }),
					}),
				};
			},
			CompletedFetchRecords: ({ cursor, result, generation, asOf }) => {
				if (
					generation !== shared.generation ||
					!Equal.equals(asOf, shared.asOf)
				) {
					return { model };
				}
				return {
					model: Result.match(result, {
						onSuccess: (page) =>
							fold(model, cursor, toResults(page, recordToRow)),
						onFailure: (error) =>
							evo(model, { results: (r) => settle(r, Result.fail(error)) }),
					}),
				};
			},
			ClickedLoadMore: () =>
				Option.match(
					AsyncData.getOrElse(model.results, () => emptyResults).nextCursor,
					{
						onNone: () => ({ model }),
						onSome: (cursor) => fetchPage(model, cursor, shared),
					},
				),
			ToggledSelect: ({ id, selected }) => ({
				model: evo(model, {
					selected: (ids) =>
						selected
							? ids.includes(id)
								? ids
								: [...ids, id]
							: ids.filter((other) => other !== id),
				}),
			}),
			ToggledSelectAll: ({ selected }) => ({
				model: evo(model, {
					selected: () => (selected ? rows(model).map((row) => row.id) : []),
				}),
			}),
			ClickedDelete: () =>
				model.selected.length === 0
					? { model }
					: openDeleteDialog(evo(model, { deleteError: () => Option.none() })),
			ClickedConfirmDelete: () => {
				if (model.pendingDeletes > 0) return { model };
				const selected = rows(model).filter((row) =>
					model.selected.includes(row.id),
				);
				const nodeIds = selected
					.filter((r) => r.type === "node")
					.map((r) => r.id);
				const knotIds = selected
					.filter((r) => r.type === "knot")
					.map((r) => r.id);
				// Events cannot be deleted here (v1 warns to the console only).
				const deleting = new Set([...nodeIds, ...knotIds]);
				if (deleting.size === 0) return closeDeleteDialog(model);
				const current = AsyncData.getOrElse(model.results, () => emptyResults);
				return {
					model: evo(model, {
						snapshot: () => Option.some(current),
						pendingDeletes: () => deleting.size,
						deleteError: () => Option.none(),
						results: (results) =>
							settle(
								results,
								Result.succeed({
									...current,
									items: current.items.filter((row) => !deleting.has(row.id)),
								}),
							),
					}),
					commands: [
						...nodeIds.map((nodeId) =>
							DeleteRecord({ nodeId, generation: shared.generation }),
						),
						...knotIds.map((knotId) =>
							DeleteEntry({ knotId, generation: shared.generation }),
						),
					],
				};
			},
			GotDeleteDialogMessage: ({ message }) => foldDeleteDialog(model, message),
			CompletedDeleteRecord: ({ nodeId, generation, result }) =>
				completedDelete(model, shared, nodeId, generation, result),
			CompletedDeleteEntry: ({ knotId, generation, result }) =>
				completedDelete(model, shared, knotId, generation, result),
			ClickedMerge: () => {
				const [targetNodeId, sourceNodeId, ...rest] = selectedNodeIds(model);
				if (
					!targetNodeId ||
					!sourceNodeId ||
					rest.length > 0 ||
					model.merging
				) {
					return { model };
				}
				return {
					model: evo(model, { merging: () => true }),
					commands: [
						MergeRecords({
							targetNodeId,
							sourceNodeId,
							generation: shared.generation,
						}),
					],
				};
			},
			CompletedMerge: ({ sourceNodeId, generation, result }) => {
				if (generation !== shared.generation) return { model };
				const settled = evo(model, { merging: () => false });
				// v1: failure is console-only; success invalidates the search and clears the selection.
				return Result.match(result, {
					onFailure: () => ({ model: settled }),
					onSuccess: () => {
						const current = AsyncData.getOrElse(
							settled.results,
							() => emptyResults,
						);
						return refresh(
							evo(settled, {
								results: (results) =>
									settle(
										results,
										Result.succeed({
											...current,
											items: current.items.filter(
												(row) => row.id !== sourceNodeId,
											),
										}),
									),
							}),
							shared,
						);
					},
				});
			},
			// v1 seeds the editor with the record schema's rows (values empty).
			ClickedAddRecord: () =>
				openAddDialog(
					evo(model, {
						form: () => ({
							...emptyForm,
							attributes: Attributes.buildRows(undefined, recordSchema(shared)),
						}),
						saving: () => false,
					}),
				),
			GotAddDialogMessage: ({ message }) => foldAddDialog(model, message),
			ChangedName: ({ value }) => ({
				model: editForm(model, (f) => ({
					...f,
					name: { _tag: "NotValidated", value },
				})),
			}),
			ChangedRecordType: ({ value }) => ({
				model: editForm(model, (f) => ({ ...f, type: value })),
			}),
			ChangedSubtitle: ({ value }) => ({
				model: editForm(model, (f) => ({
					...f,
					subtitle: { _tag: "NotValidated", value },
				})),
			}),
			ChangedEmail: ({ value }) => ({
				model: editForm(model, (f) => ({
					...f,
					email: { _tag: "NotValidated", value },
				})),
			}),
			ChangedDescription: ({ value }) => ({
				model: editForm(model, (f) => ({
					...f,
					description: { _tag: "NotValidated", value },
				})),
			}),
			GotTagsMessage: ({ message }) => foldTags(model, message),
			ClickedRemoveTag: ({ tag }) => ({
				model: editForm(model, (f) => ({
					...f,
					tagError: Option.none(),
					tags: f.tags.filter((t) => t !== tag),
				})),
			}),
			GotAttributesMessage: ({ message }) => ({
				model: editForm(model, (f) => ({
					...f,
					attributes: Attributes.update(
						f.attributes,
						message,
						recordSchema(shared),
					),
				})),
			}),
			ClickedDismissError: () => ({
				model: editForm(model, (f) => ({ ...f, error: Option.none() })),
			}),
			SubmittedAddRecord: () =>
				model.saving ? { model } : submitAddRecord(model, shared),
			CompletedAddRecord: ({ generation, result }) => {
				if (generation !== shared.generation) return { model };
				const settled = evo(model, { saving: () => false });
				return Result.match(result, {
					onSuccess: () =>
						andThen(closeAddDialog, (m) => refresh(m, shared))(settled),
					onFailure: (error) => ({
						model: editForm(settled, (f) => ({
							...f,
							error: Option.some(toMessage(error)),
						})),
					}),
				});
			},
			CompletedFetchWeek: ({ asOf, generation, result }) =>
				generation !== shared.generation || !Equal.equals(asOf, shared.asOf)
					? { model }
					: {
							model: evo(model, {
								keepInTouch: (week) => settle(week, result),
							}),
						},
			GotCardMessage: ({ message }) =>
				Cards.Message.match<Return>(message, {
					ClickedSkip: ({ recordId }) => ({
						model: evo(model, { keepInTouchError: () => Option.none() }),
						commands: [
							SkipCard({ recordId, generation: shared.generation }),
							Track({
								event: "keep_in_touch_card_action",
								props: { action: "skip" },
							}),
						],
					}),
					ClickedSwap: ({ recordId }) => ({
						model: evo(model, { keepInTouchError: () => Option.none() }),
						commands: [
							SwapCard({ recordId, generation: shared.generation }),
							Track({
								event: "keep_in_touch_card_action",
								props: { action: "swap" },
							}),
						],
					}),
					// KTD4 host half: prefill capture and focus the editor (R7).
					ClickedReachOut: ({ recordId }) => {
						const week = AsyncData.getOrElse(model.keepInTouch, () => null);
						const card = week?.cards.find((c) => c.record.id === recordId);
						if (card === undefined) return { model };
						return {
							model: editCapture(model, (c) => ({
								...c,
								picker: Picker.choose(c.picker, {
									id: recordId,
									name: card.record.name,
								}),
								fromCard: Option.some(recordId),
							})),
							commands: [FocusEditor({ id: AddEntry.EDITOR_ID })],
						};
					},
				}),
			CompletedSkip: ({ generation, result }) =>
				completedCardAction(
					model,
					shared,
					generation,
					result,
					"keepInTouch.error.skipChanged",
					true,
				),
			CompletedSwap: ({ generation, result }) =>
				completedCardAction(
					model,
					shared,
					generation,
					result,
					"keepInTouch.error.noSwap",
					false,
				),
			CompletedTrack: () => ({ model }),
			CompletedEditorAction: () => ({ model }),
			CompletedFetchRecent: ({ generation, result }) =>
				generation !== shared.generation || Option.isSome(shared.asOf)
					? { model }
					: {
							model: evo(model, {
								recent: (recent) =>
									settle(
										recent,
										Result.map(result, (page) =>
											page.data.slice(0, RECENT_LIMIT),
										),
									),
							}),
						},
			GotPickerMessage: ({ message }) => {
				const folded = foldPicker(shared)(model, message);
				return { ...folded, model: syncFromCard(folded.model) };
			},
			GotAddEntryMessage: ({ message }) =>
				foldAddEntry(model, shared)(model, message),
			CompletedAddEntries: ({ nodeId, request, generation, result }) => {
				if (generation !== shared.generation) return { model };
				return Result.match(result, {
					onSuccess: () => {
						const fromCard = Option.exists(
							model.capture.fromCard,
							(id) => id === nodeId,
						);
						// Edits made while the save was in flight survive; only what was submitted resets.
						const resetDraft = AddEntry.stillDrafts(
							model.capture.addEntry,
							request,
						);
						const resetPicker = Option.exists(
							model.capture.picker.chosen,
							(chosen) => chosen.id === nodeId,
						);
						const fetched = loadFeed(
							editCapture(model, (c) => ({
								picker: resetPicker ? Picker.init : c.picker,
								addEntry: resetDraft
									? AddEntry.succeeded(c.addEntry)
									: evo(c.addEntry, { submitting: () => false }),
								fromCard: resetPicker ? Option.none() : c.fromCard,
							})),
							shared.route,
							shared,
						);
						// The form reset does not reach the mounted editor by itself (frozen Mount args).
						return {
							model: fetched.model,
							commands: [
								...(fetched.commands ?? []),
								...(resetDraft
									? [SetEditorText({ id: AddEntry.EDITOR_ID, text: "" })]
									: []),
								Track({ event: "entry_created_from_home", props: {} }),
								...(fromCard
									? [Track({ event: "entry_created_from_card", props: {} })]
									: []),
							],
						};
					},
					onFailure: (error) => ({
						model: editCapture(model, (c) => ({
							...c,
							addEntry: AddEntry.failed(c.addEntry, toMessage(error)),
						})),
					}),
				});
			},
		}),
};
